import {Menu} from '@headlessui/react';
import {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';

import useAuth, {LoggedInAuth, useCurrentOrganization} from 'src/hooks/useAuth';
import {shouldShowOrgSwitcherUI} from 'src/lib/api/auth';
import featureFlags from 'src/lib/featureFlags';
import {makeUuidPrefix} from 'src/lib/routeUtils';
import * as tw from 'src/lib/tailwindClasses';

import Avatar from './Avatar';
import {useStickyHeader} from './StickyHeaderContext';
import {useTranslations} from './TranslationsContext';

export default function Header({isPublic = false}: {isPublic?: boolean}) {
  const auth = useAuth();
  const currentOrg = useCurrentOrganization()!;
  const {t} = useTranslations();
  const {headerSubtitle} = useStickyHeader();
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled((prev) => {
        // require scrolling past 20px to compact, but only
        // expand back when near the very top to avoid jitter.
        if (!prev) return window.scrollY > 20;
        return window.scrollY > 2;
      });
    };
    window.addEventListener('scroll', handleScroll, {passive: true});
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav className="sticky top-0 z-40 bg-white">
      <div
        className={`border-b-2 border-gray-100 transition-all duration-200 ${isScrolled ? 'py-2' : 'py-6'}`}
      >
        <div className="container mx-auto px-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Link
              to={auth.status === 'logged in' ? `/${makeUuidPrefix(currentOrg.id)}/projects` : '/'}
            >
              <div className="flex justify-start items-center">
                <img className="h-7 w-auto" src="/assets/hf-logo.svg" alt={t('logoAlt')} />
              </div>
            </Link>
            {headerSubtitle && (
              <span className="text-sm text-gray-500 truncate max-w-md">{headerSubtitle}</span>
            )}
          </div>
          {isPublic ? (
            <div className="flex items-center text-right">
              <a
                className={`${tw.link}`}
                href="https://upstream.tech/hydroforecast?utm_source=hf-public"
              >
                {t('learnMore')} ⇾
              </a>
            </div>
          ) : (
            auth.status == 'logged in' && (
              <div className="flex items-center gap-6">
                <HelpMenu />
                <UserMenu auth={auth} />
              </div>
            )
          )}
        </div>
      </div>
    </nav>
  );
}

function UserMenu({auth}: {auth: LoggedInAuth}) {
  const {t} = useTranslations();

  const userName = auth.userProfile ? auth.userProfile.name : t('defaultUserName');
  const currentOrg = useCurrentOrganization()!;

  const avatarClassNames = 'h-8 w-8 rounded-full bg-gray-100';
  const avatar = auth.userProfile ? (
    <Avatar className={avatarClassNames} user={auth.userProfile} />
  ) : (
    <div className={avatarClassNames} />
  );

  return (
    // h-7 matches the logo's height so the avatar (absolutely positioned, so it
    // adds no height) doesn't grow the header.
    <Menu as="div" className="relative h-7 w-8">
      <Menu.Button
        aria-label={t('accountMenu')}
        className="absolute right-0 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full cursor-pointer hover:ring-2 hover:ring-gray-300 hover:ring-offset-2"
      >
        {avatar}
      </Menu.Button>
      {/* Click-to-open: the menu stays open until you pick an item, click
          outside, or press Escape — so moving the mouse diagonally onto an item
          never dismisses it. Opens below the bar with a small gap (mt-2). */}
      <Menu.Items className={menuItemsClass}>
        <div className={`${tw.floatingDropdownItem} text-xs`}>
          {userName}
          <br />
          <span className="font-medium">{currentOrg.name}</span>
        </div>
        <hr className="my-0" />
        {currentOrg && (
          <Menu.Item>
            {({active}) => (
              <Link to={`/${makeUuidPrefix(currentOrg.id)}/settings`} className={itemClass(active)}>
                {t('settings')}
              </Link>
            )}
          </Menu.Item>
        )}
        {featureFlags.EVAL_DASHBOARD(auth.userProfile) && currentOrg && (
          <Menu.Item>
            {({active}) => (
              <Link to={`/eval`} className={itemClass(active)}>
                {t('evaluation')}
              </Link>
            )}
          </Menu.Item>
        )}
        {featureFlags.SETUP_WIZARD(auth.userProfile) && currentOrg && (
          <Menu.Item>
            {({active}) => (
              <Link to={`/${makeUuidPrefix(currentOrg.id)}/setup`} className={itemClass(active)}>
                Setup wizard
              </Link>
            )}
          </Menu.Item>
        )}
        {shouldShowOrgSwitcherUI(auth.profileOrganizations) && (
          <Menu.Item>
            {({active}) => (
              // Deliberately a plain link: pairing setCurrentOrganization with
              // navigation cancels the navigation — see its doc in useAuth.ts
              // and OrgSwitch.test.tsx. The switcher page doesn't need the org
              // changed; WithLoggedInAuth re-derives it from the URL prefix.
              <Link to="/organizations" className={itemClass(active)}>
                {t('switchOrganization')}
              </Link>
            )}
          </Menu.Item>
        )}
        {auth.currentOrganization && (
          <Menu.Item>
            {({active}) => (
              <Link to="/signout" className={itemClass(active)}>
                {t('signOut')}
              </Link>
            )}
          </Menu.Item>
        )}
      </Menu.Items>
    </Menu>
  );
}

// Shared geometry for both header dropdown panels: right-aligned, dropping below
// the bar with a small gap, sized to content, and clipping the full-bleed hover
// rows to the rounded corners.
const menuItemsClass = `right-0 top-full mt-2 w-max min-w-[11rem] max-w-xs overflow-hidden ${tw.floatingDropdownNoPad}`;

// Menu items highlight on hover and on keyboard focus (Headless UI's `active`
// state covers both) so it's clear they're interactive. no-underline keeps them
// reading as menu rows rather than inline links.
function itemClass(active: boolean) {
  return `${tw.floatingDropdownItem} no-underline ${active ? 'bg-gray-100' : ''}`;
}

export function HelpMenu() {
  const {t} = useTranslations();

  return (
    // h-7 matches the logo's height so the trigger doesn't grow the header.
    <Menu as="div" className="relative flex items-center h-7">
      <Menu.Button className="text-sm text-gray-700 hover:text-gray-900 cursor-pointer">
        {t('help')}
      </Menu.Button>
      {/* Click-to-open (stays open until dismissed); opens below the bar with a small gap (mt-2). */}
      <Menu.Items className={menuItemsClass}>
        <Menu.Item>
          {({active}) => (
            <a href="mailto:team@hydroforecast.com" className={itemClass(active)}>
              {t('emailUs')}
            </a>
          )}
        </Menu.Item>
        <Menu.Item>
          {({active}) => (
            <a
              href="https://hydroforecast.com/support/"
              target="_blank"
              rel="noreferrer"
              className={itemClass(active)}
            >
              {t('supportCenter')}
            </a>
          )}
        </Menu.Item>
        <Menu.Item>
          {({active}) => (
            <a
              href="https://hydroforecast.com/support/category/187-hydroforecast-api/"
              target="_blank"
              rel="noreferrer"
              className={itemClass(active)}
            >
              {t('apiDocumentation')}
            </a>
          )}
        </Menu.Item>
        <Menu.Item>
          {({active}) => (
            <a
              href="https://form.asana.com/?k=jZMA-ln7-Okp8np0q8KRqw&d=253187955686276"
              target="_blank"
              rel="noreferrer"
              className={itemClass(active)}
            >
              {t('suggestions')}
            </a>
          )}
        </Menu.Item>
      </Menu.Items>
    </Menu>
  );
}
